import { Fragment, type ReactNode } from 'react'

/**
 * Markdown for idea bodies: the subset notes and scripts actually use.
 *
 * Headings, paragraphs, lists, quotes, fenced code, rules; bold, italic,
 * inline code and links. It builds React elements rather than an HTML string,
 * so nothing in a body can become markup: ideas arrive from Claude sessions
 * and a bot as well as from Martin, and none of them should be able to put a
 * script on this page. A single newline is kept as a line break, because a
 * body typed on a phone means its line breaks.
 */
export function Markdown({ source }: { source: string }) {
  return <div className="markdown">{renderBlocks(parseBlocks(source))}</div>
}

export type Block =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; lines: string[] }
  | { type: 'list'; ordered: boolean; start: number; items: string[] }
  | { type: 'quote'; lines: string[] }
  | { type: 'code'; text: string }
  | { type: 'rule' }

const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/
const BULLET = /^\s*[-*+]\s+(.*)$/
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/
const QUOTE = /^\s*>\s?(.*)$/
const FENCE = /^\s*```/
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/

export function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]

    if (!line.trim()) {
      index++
      continue
    }

    if (FENCE.test(line)) {
      const body: string[] = []
      index++
      while (index < lines.length && !FENCE.test(lines[index])) body.push(lines[index++])
      index++ // the closing fence, or past the end
      blocks.push({ type: 'code', text: body.join('\n') })
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] })
      index++
      continue
    }

    if (RULE.test(line)) {
      blocks.push({ type: 'rule' })
      index++
      continue
    }

    if (QUOTE.test(line)) {
      const quoted: string[] = []
      while (index < lines.length && QUOTE.test(lines[index])) {
        quoted.push(QUOTE.exec(lines[index])![1])
        index++
      }
      blocks.push({ type: 'quote', lines: quoted })
      continue
    }

    const bullet = BULLET.exec(line)
    const numbered = NUMBERED.exec(line)
    if (bullet || numbered) {
      const ordered = !bullet
      const pattern = ordered ? NUMBERED : BULLET
      const items: string[] = []
      while (index < lines.length) {
        const match = pattern.exec(lines[index])
        if (match) {
          items.push(ordered ? match[2] : match[1])
        } else if (lines[index].trim() && /^\s{2,}/.test(lines[index]) && items.length) {
          // An indented continuation of the item above.
          items[items.length - 1] += `\n${lines[index].trim()}`
        } else {
          break
        }
        index++
      }
      blocks.push({ type: 'list', ordered, start: numbered ? Number(numbered[1]) : 1, items })
      continue
    }

    const paragraph: string[] = []
    while (
      index < lines.length &&
      lines[index].trim() &&
      !HEADING.test(lines[index]) &&
      !FENCE.test(lines[index]) &&
      !QUOTE.test(lines[index]) &&
      !BULLET.test(lines[index]) &&
      !NUMBERED.test(lines[index]) &&
      !RULE.test(lines[index])
    ) {
      paragraph.push(lines[index].trim())
      index++
    }
    blocks.push({ type: 'paragraph', lines: paragraph })
  }

  return blocks
}

function renderBlocks(blocks: Block[]): ReactNode {
  return blocks.map((block, key) => {
    switch (block.type) {
      case 'heading': {
        const Tag = block.level <= 2 ? 'h3' : 'h4'
        return <Tag key={key}>{inline(block.text)}</Tag>
      }
      case 'paragraph':
        return <p key={key}>{lines(block.lines)}</p>
      case 'list':
        return block.ordered ? (
          <ol key={key} start={block.start === 1 ? undefined : block.start}>
            {block.items.map((item, i) => (
              <li key={i}>{lines(item.split('\n'))}</li>
            ))}
          </ol>
        ) : (
          <ul key={key}>
            {block.items.map((item, i) => (
              <li key={i}>{lines(item.split('\n'))}</li>
            ))}
          </ul>
        )
      case 'quote':
        return <blockquote key={key}>{lines(block.lines)}</blockquote>
      case 'code':
        return (
          <pre key={key}>
            <code>{block.text}</code>
          </pre>
        )
      case 'rule':
        return <hr key={key} />
    }
  })
}

function lines(texts: string[]): ReactNode {
  return texts.map((text, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {inline(text)}
    </Fragment>
  ))
}

/**
 * Inline spans, left to right: the earliest match wins, and bold is tried
 * before italic so `**` is never read as two empty emphases.
 */
const INLINE =
  /(`[^`]+`)|(\*\*[^*]+?\*\*|__[^_]+?__)|(\*[^*\s][^*]*?\*|(?<![\w])_[^_\s][^_]*?_(?![\w]))|(\[[^\]]+\]\([^)\s]+\))|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g

export function inline(text: string): ReactNode {
  const out: ReactNode[] = []
  let last = 0
  let key = 0
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0
    if (at > last) out.push(text.slice(last, at))
    const [whole, code, bold, italic, link, url] = match
    if (code) out.push(<code key={key++}>{code.slice(1, -1)}</code>)
    else if (bold) out.push(<strong key={key++}>{inline(bold.slice(2, -2))}</strong>)
    else if (italic) out.push(<em key={key++}>{inline(italic.slice(1, -1))}</em>)
    else if (link) {
      const [, label, href] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(link) ?? []
      out.push(safeLink(href, inline(label), key++) ?? whole)
    } else if (url) out.push(safeLink(url, url, key++) ?? whole)
    last = at + whole.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out.length === 1 ? out[0] : out
}

function safeLink(href: string, label: ReactNode, key: number): ReactNode | null {
  if (!/^(https?:|mailto:)/i.test(href)) return null
  return (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  )
}
